import { useEffect, useState } from 'react'
import { useMatches } from 'react-router-dom'
import type { I18nKey } from '@/lib/i18n/types'
import type { RouteMeta } from '@/lib/routing/routing.types'
import { useTranslation } from './use-translation'

interface MatchWithMeta {
  id: string
  pathname: string
  params: Record<string, string>
  data: unknown
  handle?: {
    meta?: RouteMeta
  }
}

interface UseTitleOptions {
  fallbackTitle?: string
  updateDocument?: boolean
  returnTitle?: boolean
}

/**
 * Hook for managing and updating the page title based on route metadata.
 * It can update the document's title, return the current title state, or both.
 *
 * @param options - Configuration options
 * @returns An object containing the title and a function to update it if `returnTitle` is true, otherwise returns undefined.
 */
export function usePageTitle(options: UseTitleOptions = {}) {
  const {
    fallbackTitle = 'sidebar.appName',
    updateDocument = true,
    returnTitle = false,
  } = options
  const t = useTranslation()

  const matches = useMatches() as MatchWithMeta[]
  const [title, setTitle] = useState(t(fallbackTitle as I18nKey))

  const routeTitleKey =
    matches
      .slice()
      .reverse()
      .find((match) => match.handle?.meta?.title)?.handle?.meta?.title ?? fallbackTitle

  const routeTitle = t(routeTitleKey as I18nKey)

  useEffect(() => {
    if (returnTitle) {
      setTitle(routeTitle)
    }

    if (updateDocument) {
      document.title = routeTitle
    }
  }, [routeTitle, updateDocument, returnTitle, t])

  return returnTitle ? { title, setTitle } : undefined
}
